/**
 * As redes que o robô enxerga, em forma de escolher — funções puras, comuns à
 * Frota (ordem `escanear`) e à USB (`WIFI_SCAN`). A lista já vem filtrada pelo
 * firmware (`WifiScanPolicy.h`, spec 3.5: sem 5 GHz, sem 802.1X, SSID repetido
 * agrupado); aqui ela só é ordenada e rotulada. Agrupar e esconder SSID vazio
 * de novo é barato e não depende de o firmware acertar.
 */

export type RedeVista = { ssid: string; rssi: number; auth: string }

/** Os quatro degraus da spec 3.5. `barras` é quantas das 4 acendem. */
export type Sinal = { barras: 1 | 2 | 3 | 4; rotulo: 'forte' | 'bom' | 'fraco' | 'muito fraco' }

export function sinalDoRssi(rssi: number): Sinal {
  if (rssi >= -60) return { barras: 4, rotulo: 'forte' }
  if (rssi >= -72) return { barras: 3, rotulo: 'bom' }
  if (rssi >= -82) return { barras: 2, rotulo: 'fraco' }
  return { barras: 1, rotulo: 'muito fraco' }
}

/** O nome que o firmware dá a `WIFI_AUTH_OPEN`. */
export function ehAberta(auth: string): boolean {
  return auth.toLowerCase() === 'open'
}

export const AVISO_REDE_ABERTA =
  'Rede aberta costuma ter portal de login, e o robô não passa dele.'

/** Uma linha por SSID (a de maior sinal), sem SSID vazio, da mais forte à mais fraca. */
export function ordenarRedes(redes: readonly RedeVista[]): RedeVista[] {
  const melhor = new Map<string, RedeVista>()
  for (const r of redes) {
    if (!r.ssid) continue
    const atual = melhor.get(r.ssid)
    if (!atual || r.rssi > atual.rssi) melhor.set(r.ssid, r)
  }
  return [...melhor.values()].sort((a, b) => b.rssi - a.rssi || a.ssid.localeCompare(b.ssid))
}

const bytes = (s: string) => new TextEncoder().encode(s).length

/**
 * Os limites do firmware, em BYTES (`String::length()`): SSID de 1 a 32, senha
 * até 63. São os mesmos de `robo/wifi_remoto.py` e de `problemaNoWifi` da USB.
 * Recebe o tamanho da senha, e não a senha: a tela da Frota não a guarda.
 */
export function problemaNaRede(ssid: string, bytesDaSenha: number): string | null {
  if (bytes(ssid) === 0) return 'Escolha uma rede ou informe o nome da rede oculta.'
  if (bytes(ssid) > 32) return 'O nome da rede passa de 32 bytes, e o robô não aceita.'
  if (bytesDaSenha > 63) return 'A senha passa de 63 bytes, e o robô não aceita.'
  return null
}

export function bytesUtf8(s: string): number {
  return bytes(s)
}
